import NumberFlow from "@number-flow/react"
import { Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"

import { useAnimatedNumber } from "../../hooks/useAnimatedNumber"
import InfoTooltip from "../InfoTooltip/InfoTooltip"
import Private from "../Private"
import { formatKpiValue } from "./formatKpiValue"

import styles from "./kpiTabs.module.less"

const { Text } = Typography

// One dashboard KPI: label with its tooltip, the animated figure with an optional context note
// beside it (`tab.note`), a secondary line, and the same figure over the comparison window while a
// comparison is on (`tab.comparison`).
// `readOnly` draws it as a plain stat, without the click, underline and wash of a tab.
const KpiTab = ({ tab, active, onSelect, readOnly, className }) => {
  const { t } = useTranslation()
  const animatedValue = useAnimatedNumber(tab.value)
  const Root = readOnly ? "div" : "button"
  const figure = (
    <Private>
      {tab.value == null ? (
        "-"
      ) : (
        <>
          {/* en-US whatever the browser's language, as the lines under it: "2,064", "8.4%". */}
          <NumberFlow
            value={animatedValue}
            locales="en-US"
            prefix={tab.prefix || ""}
            suffix={tab.suffix || ""}
            format={{
              minimumFractionDigits: tab.decimals ?? 0,
              maximumFractionDigits: tab.decimals ?? 0
            }}
          />
          {/* "3.4 days" */}
          {tab.unit ? <span className={styles.unit}>{tab.unit}</span> : null}
        </>
      )}
    </Private>
  )

  return (
    <Root
      {...(readOnly ? {} : { type: "button", onClick: () => onSelect(tab.key) })}
      className={cls(
        styles.tab,
        { [styles.active]: active, [styles.readOnly]: readOnly },
        className
      )}
    >
      {!readOnly && <span aria-hidden="true" className={styles.underline} />}
      <Flex gap={4} align="center">
        <Text className={styles.label} ellipsis={{ tooltip: tab.label }}>
          {tab.label}
        </Text>
        {tab.tooltip && <InfoTooltip title={t(tab.tooltip)} />}
      </Flex>
      <Text className={cls(styles.value, { [styles.valueWithNote]: tab.note })}>
        {tab.note ? (
          <>
            <span>{figure}</span>
            <span className={styles.note}>{tab.note}</span>
          </>
        ) : (
          figure
        )}
      </Text>
      {/* A change or status badge (`tab.badge`) leads the secondary line; without one the line
          renders exactly as before. */}
      {tab.badge ? (
        <Flex gap={8} align="center" className={styles.secondaryRow}>
          {tab.badge}
          {tab.secondary ? (
            <Text className={styles.secondary}>
              {tab.secondaryPrivate ? <Private>{tab.secondary}</Private> : tab.secondary}
            </Text>
          ) : null}
        </Flex>
      ) : tab.secondary ? (
        <Text className={styles.secondary}>
          {tab.secondaryPrivate ? <Private>{tab.secondary}</Private> : tab.secondary}
        </Text>
      ) : null}
      {tab.comparison != null ? (
        <Text type="secondary" className={styles.secondary}>
          {/* Money is always masked; a page that hides every figure sets `comparisonPrivate`. */}
          <Private disabled={!tab.prefix && !tab.comparisonPrivate}>
            {`${formatKpiValue(tab)(tab.comparison)} ${tab.comparisonLabel || t("previous period")}`}
          </Private>
        </Text>
      ) : null}
    </Root>
  )
}

export default KpiTab
